import clsx from 'clsx';
import { camelCase, kebabCase } from 'lodash-es';
import { Trans, useTranslation } from 'next-i18next/pages';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { Fragment, useCallback, useEffect, useState } from 'react';

import { ReactComponent as Chevron } from '@pflegia/ui/icons/chevron.svg';
import { isDefined } from '@pflegia/util/functions/type-guards';

import FaqPageJsonLdBase, { FaqObject } from '../faq-page-json-ld/faq-page-json-ld-base';
import RelatedLocations from './RelatedLocations';
import { JOBS_ROUTE } from './constants';
import { AccordionItemProps } from './types';

const LANDING_PAGE = 'landingPage';

const RelatedJobTitles: React.FC<Pick<AccordionItemProps, 'relatedJobTitles' | 'location'>> = ({ relatedJobTitles, location }) =>
  relatedJobTitles ? (
    <>
      {relatedJobTitles.map((jobTitle, index) => (
        <Link
          key={jobTitle.id}
          onClick={event => event.stopPropagation()}
          href={`${JOBS_ROUTE}/${kebabCase(camelCase(jobTitle.title))}/${kebabCase(location)}`}
          className='text-primary hover:underline'>
          {jobTitle.title}
          {index !== relatedJobTitles.length - 1 && ', '}
        </Link>
      ))}
    </>
  ) : null;

const AccordionItem: React.FC<AccordionItemProps> = ({
  faq,
  active,
  anchorId,
  onToggle,
  namespace,
  translatedJobTitle,
  location,
  sourceDomainName,
  relatedJobTitles,
  relatedLocations,
  components = {},
  values = {},
}) => {
  const { t } = useTranslation(namespace, { keyPrefix: 'questionsAndAnswers' });

  const handleToggle = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      onToggle();
    },
    [onToggle],
  );

  if (faq === 'otherPopularLocations' && relatedLocations?.length === 0) return null;
  if (faq === 'otherPopularJobTitles' && relatedJobTitles?.length === 0) return null;

  const valuesByNamespace = {
    domain: sourceDomainName,
    translatedJobTitle,
    location,
    ...values,
  };

  const isObjectFaq = typeof faq === 'object' && isDefined(faq);
  const questionContent = isObjectFaq ? faq.question : <Trans i18nKey={`${faq}.question`} t={t} values={valuesByNamespace} />;

  const answerContent = isObjectFaq ? (
    faq.answer
  ) : (
    <Trans
      i18nKey={`${faq}.answer`}
      t={t}
      values={valuesByNamespace}
      components={{
        RelatedJobTitles: <RelatedJobTitles relatedJobTitles={relatedJobTitles} location={location} />,
        RelatedLocations: <RelatedLocations relatedLocations={relatedLocations} translatedJobTitle={translatedJobTitle} />,
        ...components,
      }}
    />
  );

  return (
    <li id={anchorId} className={clsx('border-b border-b-gray-300 last:border-b-0', anchorId && 'scroll-mt-24')}>
      <details open={active} onClick={handleToggle}>
        <summary className='details-marker:hidden w-full cursor-pointer list-none text-left'>
          <h3 className='text-primary flex items-center justify-between py-4 text-base font-medium lg:py-11 lg:text-lg'>
            <span>{questionContent}</span>
            <Chevron className={clsx('text-primary shrink-0 transition-transform lg:h-10 lg:w-10', active ? '-rotate-90' : 'rotate-90')} />
          </h3>
        </summary>
        <div className='mt-2 mb-6 text-sm lg:mt-0 lg:mb-8 lg:text-base'>{answerContent}</div>
      </details>
    </li>
  );
};

type QuestionsAndAnswersProps = {
  components?: Record<string, React.ReactElement>;
  faqs: (FaqObject | string)[];
  headerClassName?: string;
  headingText?: string;
  location?: string;
  namespace?:
    | typeof LANDING_PAGE
    | 'jobDetails'
    | 'jobBoard'
    | 'jobListings'
    | 'ueber-pflegia'
    | 'faq'
    | 'gehaltsrechner'
    | 'gehalt'
    | 'regionenLandingPage'
    | 'weiterbildungen';
  processedSchemaUrls?: Record<string, string | Record<string, string>>;
  relatedJobTitles?: { id: string; title: string }[];
  relatedLocations?: string[];
  showHeading?: boolean;
  sourceDomainName?: string;
  translatedJobTitle?: string;
  values?: Record<string, string | number | undefined>;
  withAnchors?: boolean;
};

const QuestionsAndAnswers: React.FC<QuestionsAndAnswersProps> = ({
  faqs,
  headingText,
  namespace = LANDING_PAGE,
  showHeading = true,
  withAnchors = false,
  ...rest
}) => {
  const { t } = useTranslation(namespace, { keyPrefix: 'questionsAndAnswers' });
  const router = useRouter();
  const [clicked, setClicked] = useState(-1);
  const { translatedJobTitle, location, relatedJobTitles, relatedLocations } = rest;

  const anchorIds = faqs.map(faq => (withAnchors && typeof faq === 'string' ? kebabCase(faq) : ''));
  const anchorIdsKey = anchorIds.join(',');

  useEffect(() => {
    if (!withAnchors) return undefined;

    const openFromHash = () => {
      const hash = globalThis.location.hash.slice(1);
      const index = hash ? anchorIdsKey.split(',').indexOf(hash) : -1;
      if (index !== -1) setClicked(index);
    };

    openFromHash();
    router.events.on('hashChangeComplete', openFromHash);
    globalThis.addEventListener('hashchange', openFromHash);

    return () => {
      router.events.off('hashChangeComplete', openFromHash);
      globalThis.removeEventListener('hashchange', openFromHash);
    };
  }, [anchorIdsKey, router.events, withAnchors]);

  const handleToggle = (index: number) => setClicked(current => (current === index ? -1 : index));

  return (
    <>
      <FaqPageJsonLdBase
        faqs={faqs}
        translationNamespace={namespace}
        translatedJobTitle={translatedJobTitle ?? ''}
        location={location ?? ''}
        relatedJobTitles={relatedJobTitles}
        relatedLocations={relatedLocations}
        processedSchemaUrls={rest.processedSchemaUrls}
        values={rest.values}
      />
      <section>
        {showHeading && (
          <h2
            className={clsx(
              'text-primary mb-8 text-center text-2xl font-normal wrap-break-word lg:mb-16 lg:text-4xl',
              rest.headerClassName,
            )}>
            {headingText ?? t('title')}
          </h2>
        )}
        <ul>
          {faqs.map((faq, index) => (
            <Fragment key={typeof faq === 'string' ? faq : `faq-${index}`}>
              <AccordionItem
                faq={faq}
                anchorId={anchorIds[index] || undefined}
                onToggle={() => handleToggle(index)}
                active={clicked === index}
                namespace={namespace}
                {...rest}
              />
            </Fragment>
          ))}
        </ul>
      </section>
    </>
  );
};

export default QuestionsAndAnswers;
